import Dexie, { type Table } from "dexie";

import type {
  AuditLogEntry,
  CatalogProfile,
  CatalogSnapshot,
  CWConnection,
  PendingUpload,
  QuoteRecord,
  SavedSearch,
} from "@/types/schemas";

/**
 * IndexedDB persistence for CatalogKit, backed by Dexie. All catalog memory
 * (profiles, snapshots, quotes, audit log, saved searches, pending uploads)
 * lives here; nothing ever leaves the browser.
 *
 * Indexing notes:
 * - Boolean fields (`profiles.archived`, `savedSearches.is_pinned`) are **not**
 *   indexed — IndexedDB rejects boolean values as keys — so those are filtered
 *   in memory by the CRUD layer.
 * - Compound indexes back the common access patterns (a profile's snapshots by
 *   time, a profile's audit entries by time, a profile's quotes by status).
 */

/** Default IndexedDB database name. Overridable (e.g. per-test isolation). */
export const DB_NAME = "catalogkit";

export class CatalogKitDB extends Dexie {
  // Definite-assignment: Dexie populates these from `stores()`.
  profiles!: Table<CatalogProfile, string>;
  snapshots!: Table<CatalogSnapshot, string>;
  quotes!: Table<QuoteRecord, string>;
  auditLog!: Table<AuditLogEntry, string>;
  savedSearches!: Table<SavedSearch, string>;
  pendingUploads!: Table<PendingUpload, string>;
  connections!: Table<CWConnection, string>;

  constructor(name: string = DB_NAME) {
    super(name);

    // Version 1 — initial v0.2 schema. See migrations.ts for how to evolve this.
    this.version(1).stores({
      profiles: "id, source_system, created_at, last_used_at, label",
      snapshots: "id, profile_id, uploaded_at, [profile_id+uploaded_at]",
      quotes: "id, profile_id, status, updated_at, customer_name, [profile_id+status]",
      auditLog: "id, profile_id, timestamp, category, [profile_id+timestamp]",
      savedSearches: "id, profile_id, created_at",
      pendingUploads: "id, uploaded_at",
    });

    // Version 2 — v0.3: ConnectWise connection profiles. `archived` is a
    // boolean (not indexable); filtered in memory by the CRUD layer.
    this.version(2).stores({
      connections: "id, label, site, created_at, last_connected_at",
    });
  }
}

/** The shared application database instance. */
export const db = new CatalogKitDB();
